import React, { useEffect, useState } from "react";
import {
    Card, CardBody, CardTitle, Button,
    Carousel, CarouselItem, CarouselControl, CarouselIndicators
} from "reactstrap";
import { FaTrophy } from "react-icons/fa";
import { getHighlights, addHighlight, deleteHighlight, uploadImage } from "./UpdatesApi";
import "../../assets/styles/orgchart.css";
import EditModal from "../../Components/Shared/EditModal";

const ACCENT = "#BA7517";

// const API_BASE = process.env.REACT_APP_API_URL?.replace("/api", "") || "http://www.sswhite.systems:7168";

// const getImageSrc = (url) => {
//     if (!url) return null;
//     // Extract just the /uploads/... part
//     const match = url.match(/\/uploads\/.+/);
//     if (!match) return url;
//     // Prepend the API base URL
//     return `${API_BASE}${match[0]}`;
// };

// Round HOST-1: ?? (not ||) — in production the env var is DEFINED AS
// EMPTY on purpose, meaning "same origin" (host-relative /uploads/...).
// Only a build with the var entirely ABSENT (local dev) falls back to the
// localhost API host, exactly as before.
const SERVER_BASE = process.env.REACT_APP_SERVER_BASE ?? "https://localhost:7168";

const getImageSrc = (url) => {
    if (!url) return null;
    const match = url.match(/\/uploads\/.+/);
    if (!match) return url;
    return `${SERVER_BASE}${match[0]}`;
};

const HighlightCard = ({ role }) => {
    const canEdit = ["Manager", "HR Admin"].includes(role);

    const [items, setItems] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [content, setContent] = useState("");
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState("");
    const [activeIndex, setActiveIndex] = useState(0);
    const [animating, setAnimating] = useState(false);

    const load = async () => {
        const r = await getHighlights();
        setItems(r || []);
    };

    useEffect(() => { load(); }, []);

    const onSave = async () => {
        let imageUrl = null;
        if (imageFile) imageUrl = await uploadImage(imageFile);

        const payload = {
            id: 0, type: "Highlight", title: "Highlight",
            description: content, date: new Date().toISOString(),
            imageUrl: imageUrl ?? "", employeeName: "", employeeRole: "",
            employeeImageUrl: "", eventLocation: "", eventTime: ""
        };

        try { await addHighlight(payload); } catch (err) { console.error("API ERROR:", err); }

        setModalOpen(false); setContent(""); setImageFile(null); setImagePreview("");
        load();
    };

    const onDelete = async (id) => {
        await deleteHighlight(id);
        if (activeIndex > 0 && activeIndex >= items.length - 1) setActiveIndex(activeIndex - 1);
        load();
    };

    const next = () => {
        if (animating || items.length <= 1) return;
        setActiveIndex(activeIndex === items.length - 1 ? 0 : activeIndex + 1);
    };
    const previous = () => {
        if (animating || items.length <= 1) return;
        setActiveIndex(activeIndex === 0 ? items.length - 1 : activeIndex - 1);
    };
    const goToIndex = (i) => { if (!animating) setActiveIndex(i); };

    const slides = items.map((item) => (
        <CarouselItem
            key={item.id}
            onExiting={() => setAnimating(true)}
            onExited={() => setAnimating(false)}
        >
            <div style={styles.slide}>
                {item.imageUrl ? (
                    <img
                        src={getImageSrc(item.imageUrl)}  // ✅ fixed
                        alt={item.description || "Highlight"}
                        style={styles.slideImg}
                    />
                ) : (
                    <div style={{ ...styles.slideImg, ...styles.placeholder }}>No Image Available</div>
                )}
                <div style={styles.overlay}>
                    <p style={styles.overlayText}>{item.description || "Company Highlight"}</p>
                </div>
            </div>
        </CarouselItem>
    ));

    return (
        <Card style={styles.card}>
            <CardBody style={styles.cardBody}>
                <div style={{ ...styles.accentBar, background: ACCENT }} />

                {/* Header */}
                <div style={styles.header}>
                    <CardTitle tag="h6" style={styles.title}>
                        <div style={styles.iconWrap}>
                            <FaTrophy size={15} color={ACCENT} />
                        </div>
                        Company Highlights
                    </CardTitle>

                    {canEdit && (
                        <div style={{ display: "flex", gap: "8px" }}>
                            <Button size="sm" onClick={() => setModalOpen(true)} style={{ ...styles.addBtn, background: ACCENT }}>
                                + Add
                            </Button>
                            {items.length > 0 && (
                                <Button color="danger" outline size="sm" style={styles.deleteBtn}
                                    onClick={() => onDelete(items[activeIndex]?.id)}>
                                    Delete
                                </Button>
                            )}
                        </div>
                    )}
                </div>

                {/* Carousel */}
                {items.length > 0 ? (
                    <div style={styles.carouselWrap}>
                        <Carousel activeIndex={activeIndex} next={next} previous={previous} interval={5000} ride="carousel" slide>
                            <CarouselIndicators items={items} activeIndex={activeIndex} onClickHandler={goToIndex} />
                            {slides}
                            {items.length > 1 && (
                                <>
                                    <CarouselControl direction="prev" directionText="Previous" onClickHandler={previous} />
                                    <CarouselControl direction="next" directionText="Next" onClickHandler={next} />
                                </>
                            )}
                        </Carousel>
                    </div>
                ) : (
                    <div style={styles.empty}>No company highlights available.</div>
                )}

                <EditModal
                    isOpen={modalOpen}
                    toggle={(action) => {
                        if (action === "save") onSave();
                        else { setModalOpen(false); setImagePreview(""); }
                    }}
                    title="Add Highlight"
                    content={content} setContent={setContent}
                    imageFile={imageFile}
                    setImageFile={(file) => { setImageFile(file); setImagePreview(URL.createObjectURL(file)); }}
                    imagePreview={imagePreview}
                />
            </CardBody>
        </Card>
    );
};

const styles = {
    card: { borderRadius: "16px", border: "0.5px solid #e8e8e8", boxShadow: "0 1px 6px rgba(0,0,0,0.06)", overflow: "hidden", position: "relative", height: "100%" },
    cardBody: { padding: "20px 20px 14px" },
    accentBar: { position: "absolute", top: 0, left: 0, width: "4px", height: "100%", borderRadius: "16px 0 0 16px" },
    header: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" },
    title: { margin: 0, fontWeight: 600, fontSize: "14px", color: "#2C2C2A", display: "flex", alignItems: "center", gap: "8px" },
    iconWrap: { width: "30px", height: "30px", borderRadius: "8px", background: "#FAEEDA", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
    addBtn: { border: "none", borderRadius: "8px", fontSize: "12px", padding: "4px 12px", color: "#fff" },
    deleteBtn: { fontSize: "11px", padding: "2px 8px" },
    carouselWrap: { borderRadius: "12px", overflow: "hidden" },
    slide: { position: "relative", width: "100%", height: "240px" },
    slideImg: { width: "100%", height: "240px", objectFit: "cover", display: "block", minHeight: "240px", maxHeight: "240px" },
    placeholder: { background: "#f0f0f0", display: "flex", alignItems: "center", justifyContent: "center", color: "#888", fontSize: "13px" },
    overlay: { position: "absolute", bottom: 0, left: 0, right: 0, background: "linear-gradient(transparent, rgba(0,0,0,0.6))", padding: "24px 16px 14px" },
    overlayText: { margin: 0, color: "#fff", fontSize: "13px", fontWeight: 500 },
    empty: { textAlign: "center", padding: "40px 0", color: "#888780", fontSize: "13px" },
};

export default HighlightCard;